import type { ReactNode } from 'react';
import { splitLinks } from '../../shared/links';

/**
 * Naming people, wherever there is somewhere to type.
 *
 * Lifted out of the chat screen when story comments wanted the same thing. Two
 * implementations of "what counts as a mention" would have drifted the first
 * time either was touched, and the answer has to match on both sides of the
 * wire: the composer decides who gets notified, and the renderer decides what
 * gets highlighted.
 */

/** Stands in for a uid in a draft's mention map: @FamBam means the lot. */
export const EVERYONE = '*';

/**
 * The @word being typed at the caret, if any. Anchored to a word boundary so
 * an email address in the middle of a sentence does not open the picker.
 */
export function activeMention(
  text: string,
  caret: number,
): { query: string; start: number } | null {
  const match = /(?:^|\s)@([^\s@]*)$/.exec(text.slice(0, caret));
  if (!match) return null;
  return { query: match[1], start: caret - match[1].length - 1 };
}

/**
 * Text with every `@Name` picked out.
 *
 * Names are matched longest first, so in a family with both an Anna and an
 * Anna Marie the longer one wins rather than leaving " Marie" as loose text.
 */
export function withMentions(text: string, names: string[]): ReactNode[] {
  const longestFirst = [...names].sort((a, b) => b.length - a.length);
  const out: ReactNode[] = [];
  let plain = '';
  let i = 0;

  while (i < text.length) {
    const name = text[i] === '@' && longestFirst.find((n) => n && text.startsWith(n, i + 1));
    if (name) {
      if (plain) out.push(plain);
      plain = '';
      out.push(
        <span className="mention" key={i}>
          @{name}
        </span>,
      );
      i += name.length + 1;
      continue;
    }
    plain += text[i];
    i += 1;
  }

  if (plain) out.push(plain);
  return out;
}

/**
 * The same nodes, with any web address in the plain runs made tappable.
 *
 * Applied over the output of withMentions rather than folded into it, so that
 * a name is still found inside a sentence and a link inside a name is not
 * looked for at all: the mention pass has already claimed those pieces.
 */
export function withLinks(nodes: ReactNode[]): ReactNode[] {
  const out: ReactNode[] = [];

  nodes.forEach((node, index) => {
    if (typeof node !== 'string') {
      out.push(node);
      return;
    }

    for (const [piece, at] of splitLinks(node).map((p, i) => [p, i] as const)) {
      if (!piece.link) {
        out.push(piece.text);
        continue;
      }
      out.push(
        <a
          key={`link-${index}-${at}`}
          className="in-text-link"
          href={piece.link}
          target="_blank"
          rel="noreferrer noopener"
          // The row owns press-and-hold and swipe-to-reply. Without this a
          // link inside a message answered the gesture as well as the row.
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {piece.text}
        </a>,
      );
    }
  });

  return out;
}

/**
 * Who a finished piece of text actually names.
 *
 * Only names still present count, so deleting a mention before sending unsends
 * its notification. @FamBam opens out to the whole family bar the author.
 */
export function mentionsIn(
  text: string,
  mentioned: Record<string, string>,
  everyone: string[],
  self: string,
): string[] {
  const named = Object.entries(mentioned).filter(([, name]) => text.includes(`@${name}`));
  if (named.some(([key]) => key === EVERYONE)) {
    return everyone.filter((uid) => uid !== self);
  }
  return named.map(([key]) => key);
}

/**
 * A message that named you since you last read the chat: somebody else's,
 * still there, newer than `since`, and with you among its mentions -- @FamBam
 * included, since it is stored as everyone it reached.
 */
export function mentionsMeSince(
  message: { data: { authorUid: string; mentions?: string[]; createdAt?: unknown; deletedAt?: unknown } },
  since: number,
  uid: string,
): boolean {
  const { data } = message;
  if (data.authorUid === uid || data.deletedAt) return false;
  if (!(data.mentions ?? []).includes(uid)) return false;
  const at = data.createdAt as { toMillis?: () => number } | null | undefined;
  return (at?.toMillis?.() ?? 0) > since;
}
